<template>
  <BaseModalEditCreate
    :mode="mode"
    :is-modified="isModified"
    @save="onSave"
    @dismiss-changes="onDismissChanges">
    <template #form>
      <BaseSelectDropdown
        :items="dropdownMwApplications"
        :selected-item="selectedApplication"
        :label="$t('settings.app')"
        @update:selected="handleApplication"
        name="app"
        clearable
        show-error-as-tooltip
        :error="$v.application.required.$invalid ? $v.application.required.$message : ''" />

      <BaseSelectDropdown
        :items="dropdownModules"
        :selected-item="selectedModule"
        :label="$t('settings.modules')"
        @update:selected="handleModule"
        name="module"
        clearable
        show-error-as-tooltip
        :error="$v.module.required.$invalid ? $v.module.required.$message : ''" />
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useLookupsStore } from '@/stores/lookups';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import BaseSelectDropdown from '@/components/common/BaseSelectDropdown.vue';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';
import { v4 as uuidv4 } from 'uuid';
import { type SelectDropdownItem } from '@/types/Base';
import type { MWApplication, MWModule } from '@/types/MW';
import { useI18n } from 'vue-i18n';

const emit = defineEmits(['closeModal', 'save']);
const props = withDefaults(
  defineProps<{
    mode: 'add' | 'edit';
    modules: MWModule[];
    applications: MWApplication[];
  }>(),
  {}
);

const { moduleInstallations } = storeToRefs(useLookupsStore());
const isModified = ref<boolean>(false);
const selectedApplication = ref<SelectDropdownItem | null>(null);
const selectedModule = ref<SelectDropdownItem | null>(null);
watch(
  () => selectedApplication.value,
  (newData: any) => {
    validationModel.value.application = newData || '';
  }
);
watch(
  () => selectedModule.value,
  (newData: any) => {
    validationModel.value.module = newData || '';
    isModified.value = true;
  }
);

function handleApplication(item: SelectDropdownItem) {
  selectedApplication.value = item ? item : null;
  if (
    selectedModule.value &&
    selectedApplication.value &&
    moduleInstallations.value.find((i) => i.mwModuleId === selectedModule.value?.value)
      ?.mwApplicationId != selectedApplication.value.value
  )
    selectedModule.value = null;
  isModified.value = true;
}

function handleModule(item: SelectDropdownItem) {
  selectedModule.value = item ? item : null;
  if (!selectedApplication.value && item)
    selectedApplication.value =
      dropdownMwApplications.value.find(
        (a) =>
          a.value ===
          moduleInstallations.value.find((i) => i.mwModuleId === item.value)?.mwApplicationId
      ) ?? null;
}

function onDismissChanges() {
  emit('closeModal');
}

async function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  emit('save', {
    id: uuidv4(),
    mwModuleID: selectedModule.value?.value,
    mwApplicationID: selectedApplication.value?.value,
    isBinaryAllowed: false,
    isMobileAllowed: false,
    isActive: true,
    isFromManager: true,
  });
}

const dropdownMwApplications = computed(() => {
  return props.applications.map((item) => ({
    value: item.id,
    name: item.name,
  }));
});

const dropdownModules = computed(() => {
  return props.modules
    .filter(
      (m) =>
        !selectedApplication.value ||
        moduleInstallations.value.find((i) => i.mwModuleId === m.id)?.applicationName ===
          selectedApplication.value?.name
    )
    .map((item) => {
      return { name: item.displayName, value: item.id };
    });
});

const validationModel = ref({
  application: selectedApplication.value || '',
  module: selectedModule.value || '',
});

const { t: $t } = useI18n({ useScope: 'global' });

const rules = computed(() => ({
  application: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  module: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });
</script>
